import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from '@highereducation/hen-ui';
import { useDataSourceManager } from '@de/providers/DataSourceManager';
import {
  useResultsPageConfig,
  useResultsPagePopupVisibility,
} from '@de/providers/ResultsPageConfigProvider';
import { useLeadFlow } from '@de/providers/LeadFlowProvider';
import { useSubmittedProgramsOptional } from '@de/providers/SubmittedProgramsProvider';
import { extractIteratorArray } from '@de/components/DataRepeater/extractIteratorArray';
import type { Result } from '@de/types/result';
import Spotlight from '../../Spotlight';
import ProgramSelect from '../../ProgramSelect/Default';

/**
 * Name of the DSM source the result-page shell binds the exact /
 * recommended / related / spotlight buckets onto. The popup surfaces are
 * platform-owned, so the source name + bucket paths are a fixed
 * convention here rather than authorable DSL.
 */
const RESULTS_SOURCE_NAME = 'results';

/**
 * Platform-owned bucket paths read from the `results` source payload.
 * Each surface tries its canonical bucket first, then a `<name>.data`
 * fallback to tolerate the alternate envelope shape some shells emit.
 */
const SPOTLIGHT_BUCKET_PATH = 'spotlightResults';
const SPOTLIGHT_BUCKET_FALLBACK_PATH = 'spotlight.data';
const RECOMMENDED_BUCKET_PATH = 'recommendedResults';
const RECOMMENDED_BUCKET_FALLBACK_PATH = 'recommended.data';

/**
 * Resolve a result bucket from the `results` source payload, trying the
 * canonical path first and the `<name>.data` envelope second. Returns a
 * `Result[]` (possibly empty) so callers never have to null-guard.
 */
function readResultBucket(
  sourceData: unknown,
  primaryPath: string,
  fallbackPath: string,
): Result[] {
  const rows =
    extractIteratorArray(sourceData, primaryPath) ??
    extractIteratorArray(sourceData, fallbackPath);
  return (rows ?? []) as Result[];
}

/**
 * `DslResultsPagePopups` -- the governed entry point for the Recommender
 * and Spotlight conversion surfaces on a DSL-rendered result page.
 *
 * This is a platform-owned, config-gated runtime component (no new
 * context). It mirrors the imperative `ResultsPage` popup chrome and
 * drives conversion through the **governed** lead flow
 * (`LeadFlowProvider`). Clicking a card opens the governed surface in
 * `inline` mode so its PII / submission steps render **inside this popup
 * modal** -- the user never leaves the popup view. Operators may only
 * tune approved copy via `resultsPage.recommender.modal` /
 * `resultsPage.spotlight.modal`; trigger logic and card counts stay
 * platform-owned:
 *
 *   - Spotlight takes precedence over Recommender (mirrors
 *     {@link useRecommendationPopup}): a Spotlight bucket with data wins
 *     whenever the Spotlight modal flag is enabled; the Recommender only
 *     shows when Spotlight does not and its own bucket + flag qualify.
 *     A **disabled** Spotlight never wins -- even when its bucket is
 *     populated -- so a disabled-but-populated Spotlight shows nothing
 *     unless the Recommender is itself enabled (and has data), in which
 *     case the Recommender shows. Disabling both surfaces shows neither.
 *   - The qualifying surface auto-opens on load. A `hasDismissedRef`
 *     latch ensures the popup **cannot retrigger** once the user exits,
 *     even if the DSM bucket arrays change identity on a later fetch
 *     (PRD: "cannot retrigger once exited").
 *   - Card counts are enforced inside the presentational surfaces --
 *     `Spotlight` renders `results?.[0]` (1 card) and `ProgramSelect`
 *     renders `results?.slice(0, 2)` (<=2 cards). No count prop is
 *     exposed to DSL.
 *
 * Renders `null` when no surface qualifies (flags disabled or buckets
 * empty), so non-result configs and disabled experiences are unaffected.
 *
 * Hook dependencies (`useDataSourceManager`, `useResultsPageConfig`,
 * `useLeadFlow`, `useDynamicExperienceContext`) are satisfied by the
 * provider stack `ConfigurableStep` already mounts for result configs;
 * this component is rendered as a sibling of `<DslConfig>` inside that
 * stack.
 */
const DslResultsPagePopups: React.FC = () => {
  const {
    shouldShowRecommenderModal,
    shouldShowSpotlightModal,
    getRecommenderModalCopy,
    getSpotlightModalCopy,
  } = useResultsPageConfig();
  const { getSource, updateFormState } = useDataSourceManager();
  const {
    openLeadFlow,
    closeLeadFlow,
    isOpen: isLeadFlowOpen,
    leadFlowContent,
    firstName,
  } = useLeadFlow();
  // Submitted-programs registry. Optional so the popups still
  // render standalone (e.g. tests). Forwarded to the presentational
  // surfaces so already-submitted cards reflect submitted state; the
  // actual conversion still delegates to the governed `openLeadFlow`.
  const submitted = useSubmittedProgramsOptional();
  // Copy into a mutable array — the registry exposes a `readonly` snapshot
  // but the presentational surfaces type their prop as mutable. Memoized on
  // the registry's stable `submittedResults` reference so the popup hands a
  // stable prop identity to `Spotlight` / `ProgramSelect` across no-op
  // re-renders.
  const submittedResults = useMemo(
    () => [...(submitted?.submittedResults ?? [])],
    [submitted?.submittedResults],
  );

  const resultsSourceData = getSource(RESULTS_SOURCE_NAME)?.data;

  const spotlightBucket = useMemo(
    () =>
      readResultBucket(
        resultsSourceData,
        SPOTLIGHT_BUCKET_PATH,
        SPOTLIGHT_BUCKET_FALLBACK_PATH,
      ),
    [resultsSourceData],
  );
  const recommendedBucket = useMemo(
    () =>
      readResultBucket(
        resultsSourceData,
        RECOMMENDED_BUCKET_PATH,
        RECOMMENDED_BUCKET_FALLBACK_PATH,
      ),
    [resultsSourceData],
  );

  // Platform-owned precedence: Spotlight wins over Recommender. Each
  // surface needs BOTH its config flag enabled AND a non-empty bucket.
  const showSpotlight = spotlightBucket.length > 0 && shouldShowSpotlightModal;
  const showRecommender =
    !showSpotlight &&
    recommendedBucket.length > 0 &&
    shouldShowRecommenderModal;
  const qualifies = showSpotlight || showRecommender;

  // Once the user exits the popup it must not retrigger, even when a
  // later DSM fetch hands back a fresh bucket-array identity. The ref
  // latch survives re-renders (unlike state) and is intentionally never
  // reset within a single config mount.
  const hasDismissedRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);

  // Surface the popup's open state to the surrounding
  // `ResultsPageConfigProvider` so the occluded background DSL cards
  // (via `Trackable`) defer their intersect-driven `ProductViewed`
  // until the popup is dismissed — see the provider's
  // `ResultsPagePopupVisibilityContextValue` docblock.
  const { setPopupOpen } = useResultsPagePopupVisibility();

  useEffect(() => {
    if (qualifies && !hasDismissedRef.current) {
      setIsOpen(true);
    }
  }, [qualifies]);

  useEffect(() => {
    setPopupOpen(isOpen);
    // On unmount the popup is no longer occluding anything; release the
    // gate so any still-unfired cards can observe normally.
    return () => setPopupOpen(false);
  }, [isOpen, setPopupOpen]);

  const closePopup = useCallback(() => {
    hasDismissedRef.current = true;
    setIsOpen(false);
    // Reset the governed surface too so a reopened popup starts on the
    // card view rather than a stale inline PII step.
    closeLeadFlow();
  }, [closeLeadFlow]);

  // Every conversion intent from a Spotlight / Recommender card delegates
  // to the governed lead flow, but opened `inline` so the PII surface
  // renders inside THIS popup modal -- the user never leaves the popup
  // view. The popup stays open; the modal swaps the card view for the
  // governed submission surface below.
  //
  // `/match` cards carry no TCPA copy. It arrives from the `offeringDetail`
  // source, which only fetches once `selectedOfferingId` is in formState, so
  // the id must be written before opening. Without the TCPA disclosure the
  // Jornaya script never loads and the lead is submitted without a LeadID.
  const handleProgramClick = useCallback(
    (result: Result) => {
      const offeringId = result.offeringUuid?.trim();
      if (offeringId) {
        updateFormState('selectedOfferingId', offeringId);
      }
      openLeadFlow(result, { mode: 'inline' });
    },
    [openLeadFlow, updateFormState],
  );

  if (!qualifies) {
    return null;
  }

  const copy = showSpotlight
    ? getSpotlightModalCopy()
    : getRecommenderModalCopy();
  const accessibleTitle =
    copy.title ??
    (showSpotlight ? 'Spotlight program' : 'Recommended programs');

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!open) closePopup();
      }}
    >
      {/* Modal chrome mirrors `ResultsPage/variants/Default.tsx`: the
          Recommender / Spotlight inner sections own their rounded-2xl
          surface, so the outer content drops its background and matches
          the inner radius to keep the edges flush. */}
      <Modal.Content
        size='xl'
        className='max-w-5xl p-0 max-h-[95vh] overflow-y-auto rounded-2xl bg-transparent border-0 shadow-none'
      >
        <Modal.Header className='sr-only'>
          <Modal.Title>{accessibleTitle}</Modal.Title>
        </Modal.Header>
        <div className='flex flex-col gap-md'>
          {isLeadFlowOpen ? (
            // Governed PII / submission surface, rendered in place of the
            // card view so the user stays inside the popup. The inner
            // `SingleProgramSubmission` owns its own back / close controls;
            // closing it (`closeLeadFlow`) returns to the card view.
            <div className='rounded-2xl bg-white'>{leadFlowContent}</div>
          ) : showSpotlight ? (
            <Spotlight
              results={spotlightBucket}
              firstName={firstName}
              submittedResults={submittedResults}
              title={copy.title}
              subtitle={copy.subtitle}
              disclosureTitle={copy.disclosureTitle}
              disclosureContent={copy.disclosureContent}
              onProgramClick={handleProgramClick}
              onCloseExperience={closePopup}
            />
          ) : (
            <ProgramSelect
              results={recommendedBucket}
              firstName={firstName}
              importantFactors={[]}
              submittedResults={submittedResults}
              // Governed surface: a submitted Recommender card must reopen the
              // governed lead flow (exit-strategy view) via `onProgramClick`,
              // not link out — matching `Spotlight` so the conversion boundary
              // stays platform-owned.
              governSubmittedClick
              title={copy.title}
              subtitle={copy.subtitle}
              disclosureTitle={copy.disclosureTitle}
              disclosureContent={copy.disclosureContent}
              onProgramClick={handleProgramClick}
              onCloseExperience={closePopup}
            />
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default DslResultsPagePopups;
